/**
 * Buildings made from imported schematic files, and source links.
 *
 * The schematic file itself never goes into the shared plan (other people's builds aren't ours to
 * pass around): the plan only keeps what the file is (its sha256), its size and orientation, the
 * material list and a link to where it came from. Everyone keeps the file in their own browser;
 * whoever doesn't have it sees a plain box and can get the file from the source link.
 */
import { bounds, rectCorners, type Pt } from "./geometry.ts";

export type SchematicFormatId = "litematic" | "sponge" | "mcedit" | "structure";

export interface SchematicRef {
  /** sha256 of the file (hex) — the same file on every computer has the same hash */
  hash: string;
  /** original file name */
  file: string;
  format: SchematicFormatId;
  /** x (width) · y (height) · z (length), as saved in the file (before turning) */
  size: [number, number, number];
  /** quarter turns clockwise (seen from above) */
  turns: number;
  /** mirrored along x (before turning) */
  mirror: boolean;
  /** non-air blocks */
  blocks: number;
  /** creator, from the file's metadata */
  author?: string;
  /** what you need to build it: item id → count, largest first (a bill of materials, not the build) */
  materials?: [string, number][];
}

const FORMATS: SchematicFormatId[] = ["litematic", "sponge", "mcedit", "structure"];

/** Footprint size (x, z) after turning. */
export function schematicDims(ref: Pick<SchematicRef, "size" | "turns">): [number, number] {
  const [w, , l] = ref.size;
  return ref.turns & 1 ? [l, w] : [w, l];
}

/** Footprint corners with the min corner at (x0, z0). */
export function schematicFootprint(ref: Pick<SchematicRef, "size" | "turns">, x0: number, z0: number): Pt[] {
  const [w, l] = schematicDims(ref);
  return rectCorners([x0, z0], [x0 + w - 1, z0 + l - 1]);
}

/** Footprint centred on the block `at` (where the mouse is while placing). */
export function schematicFootprintAt(ref: Pick<SchematicRef, "size" | "turns">, at: Pt): Pt[] {
  const [w, l] = schematicDims(ref);
  return schematicFootprint(ref, at[0] - Math.floor((w - 1) / 2), at[1] - Math.floor((l - 1) / 2));
}

/** Min corner of an object's footprint (where the schematic's box starts). */
export function schematicOrigin(points: Pt[]): Pt {
  const b = bounds(points);
  return [b.x0, b.z0];
}

/** Orientation after mirroring (optional) and then turning by `k` quarter turns — e.g. when a template is placed. */
export function turnSchematic(ref: Pick<SchematicRef, "turns" | "mirror">, k: number, mirror = false): { turns: number; mirror: boolean } {
  // mirror ∘ rotate(t) = rotate(-t) ∘ mirror
  const t = mirror ? k - ref.turns : k + ref.turns;
  return { turns: ((t % 4) + 4) % 4, mirror: mirror ? !ref.mirror : ref.mirror };
}

/**
 * A source link as typed/pasted by someone: http(s) only (no javascript: …), "www.…" gets https://.
 * Returns null for anything that isn't a web link.
 */
export function normalizeSourceUrl(input: string | null | undefined): string | null {
  let s = (input ?? "").trim();
  if (!s) return null;
  if (/^www\./i.test(s)) s = `https://${s}`;
  if (!/^https?:\/\//i.test(s) || /\s/.test(s) || s.length > 1000) return null;
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:" ? u.href : null;
  } catch {
    return null;
  }
}

/** "https://www.minecraft-schematics.com/schematic/123/" → "minecraft-schematics.com" */
export function sourceHost(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

/** Validate a schematic reference from a plan file / collaborator (null if unusable). */
export function parseSchematicRef(raw: unknown): SchematicRef | null {
  const r = raw as Partial<SchematicRef> | null;
  if (!r || typeof r !== "object" || typeof r.hash !== "string" || !/^[0-9a-f]{64}$/.test(r.hash)) return null;
  const size = Array.isArray(r.size) && r.size.length === 3 && r.size.every((v) => Number.isInteger(v) && v > 0 && v <= 100000) ? (r.size as [number, number, number]) : null;
  if (!size) return null;
  const materials = Array.isArray(r.materials)
    ? r.materials.filter((m) => Array.isArray(m) && typeof m[0] === "string" && m[0].length < 200 && Number.isFinite(m[1])).slice(0, 500).map((m) => [m[0], Math.max(0, Math.round(m[1]))] as [string, number])
    : undefined;
  return {
    hash: r.hash,
    file: String(r.file ?? "schematic").slice(0, 200),
    format: FORMATS.includes(r.format as SchematicFormatId) ? (r.format as SchematicFormatId) : "sponge",
    size,
    turns: Number.isInteger(r.turns) ? (((r.turns as number) % 4) + 4) % 4 : 0,
    mirror: !!r.mirror,
    blocks: Number.isFinite(r.blocks) ? Math.max(0, Math.round(r.blocks as number)) : 0,
    ...(typeof r.author === "string" && r.author ? { author: r.author.slice(0, 200) } : {}),
    ...(materials ? { materials } : {}),
  };
}
